<template>
  <div class="result-grid__wrapper">
    <FilterChipContainer @userAction="handleUserAction" />
    <OverlayScrollbars 
      :options="scrollbarOptions"
      @os-scroll="handleScroll"
      class="results-scroller" 
      ref="resultsScrollbar">
      <div class="results-options">
        <span>
          <span class="loading-spinner" v-if="loading">
            <svg xmlns="http://www.w3.org/2000/svg" height="1rem" viewBox="0 0 24 24"><path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z"><animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite"/></path></svg>
          </span>
          <span>{{results.length}} of {{ getTotalFound()?.toLocaleString() }} Results</span>
        </span>
        <span class="sort-settings">
          Sort:
          <VoyagerPopper>
            <span class="current-sort">{{sortDisplayValue}}</span>
            <template #content>
              <div v-for="sortOption of sort.available" 
                    :key="sortOption.field"
                    class="sort-option"
                    @click="updateSort(sortOption.field)">
                {{sortOption.display}}
              </div>
            </template>
          </VoyagerPopper>
          <ArrowUpIcon 
            :style="{
              transform: sort.dir === 'asc' ? '' : 'rotate(180deg)'
            }"
            class="sort-dir-toggle"
            @click="toggleSortDir"/>
        </span>
      </div>
      <div class="card-grid">
        <TransitionGroup name="grid">
          <VoyagerCard v-for="(resultItem, index) in $props.results" 
            :result="resultItem"
            :key="resultItem.id"
            :isInCart="isItemInCart(resultItem.id).value"
            :data-result-id="resultItem.id"
            :style="gridLoadingStyle(index)"
            @userAction="handleUserAction"/>
        </TransitionGroup>

        <VoyagerCard
          v-for="loadingCard in 10"
          :key="loadingCard"
          :result="{}"
          v-if="loading"
          :loading="true">
          <template #thumbnail>
            <div class="skeleton-thumb"></div>
          </template>
          <template #header>
            <div class="skeleton-text"></div>
          </template>
          <template #subheader>
            <div class="skeleton-text"></div>
          </template>
          <template #content>
            <div class="skeleton-content"></div>
          </template>
        </VoyagerCard>
      </div>
    </OverlayScrollbars>
    <button v-if="scrollTopButtonVisible"
      @click="resetResultsScroll"
      class="reset-scroll">
      Return to Top 
      <ArrowUpIcon />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, PropType, Ref, computed } from 'vue';
import { VoyagerCard, VoyagerPopper, VoyagerResult } from 'VoyagerComponents';
import { useResultsStore } from '@/store/results';
import { ArrowUpIcon } from 'vue-tabler-icons'; 
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
import { useCartStore } from '@/store/cart';
import FilterChipContainer from './FilterChipContainer.vue';

const props = defineProps({
  sort: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: true
  },
  results: {
    type: Array as PropType<VoyagerResult[] | ReadonlyArray<VoyagerResult>>,
    required: false,
    default: () => []
  }
})

const emit = defineEmits(['toggleSortDir', 'userAction', 'setSortKey', 'loadNextPage']),
  updateSort = (key: string) => emit('setSortKey', key),
  toggleSortDir = () => emit('toggleSortDir'),
  handleUserAction = ($event) => emit('userAction', $event),
  { getTotalFound } = useResultsStore(),
  { isItemInCart } = useCartStore(),
  scrollTopButtonVisible = ref(false),
  resultsScrollbar: Ref<typeof OverlayScrollbarsComponent | null> = ref(null),
  resetResultsScroll = () => {
    const { viewport } = resultsScrollbar.value?.osInstance().elements();
    viewport.scrollTo({top: 0, behavior: 'smooth'})
  },
  scrollbarOptions = {
    scrollbars: {
      theme: 'os-theme-light',
    },
  },
  handleScroll = (instance) => {
    if(props.loading) return;
    const { scrollTop, scrollHeight, clientHeight } = instance.elements().viewport;
    const distanceToBottom = Math.abs(scrollHeight - scrollTop - clientHeight);
    scrollTopButtonVisible.value = scrollTop > clientHeight;
    if(distanceToBottom < 100) emit('loadNextPage');
  },
  gridLoadingStyle = (index: number) => {
    const timing = `${(index % 48)/20 + 1}s`;
    return {'transition': `all ${timing} ease-in`};
  },
  sortDisplayValue = computed(() => {
    const sortOption = props.sort.available.find(a => a.field === props.sort.field);
    return sortOption?.display || props.sort.field;
  });

</script>

<style scoped lang="scss">
.result-grid__wrapper {
  position: relative;
  height: 100%;
}

.results-scroller {
  height: 100%;
  padding-right: 1rem;
  .results-options {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: space-between;
    color: var(--color-text-primary);
    font-size: var(--font-size-large);
    padding: 0rem 1rem 0.25rem;
    span {
      align-self: flex-end;
      white-space: nowrap;
    }
    .loading-spinner {
      stroke: var(--color-text-primary);
      fill: var(--color-text-primary);
      padding-right: .5rem;
    }
  }
}

.card-grid {
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-auto-rows: 20rem;
  justify-content: center;
  padding-bottom: 10rem;
  padding-left: 0.5rem;
}

.grid-enter-from,
.grid-leave-to {
  opacity: 0;
}

.grid-leave-active {
  // override the gridLoadingStyle transition duration when removing items.
  transition-duration: 0ms !important;
}

.sort-settings {
  user-select: none;
  display: flex;
  align-items: center;
  .current-sort {
    border-radius: var(--border-radius);
    padding: 0 .5rem;
    &:hover {
      cursor: pointer;
    }
  }
  .sort-dir-toggle {
    position: relative;
    right: 4px;
    margin-left: 0.25rem;
    transition: transform 0.25s ease 0s;
    height: var(--font-size-large);
    width: var(--font-size-large);
    border-radius: 1rem;
    &:hover {
      background-color: var(--color-background-light);
      cursor: pointer;
    }
  }
  .sort-option {
    background-color: var(--color-background-light);
    padding: 0px 1rem;
    &:last-child {
      padding-bottom: .25rem;
    }
    &:hover {
      background-color: var(--color-action-background-primary);
      cursor: pointer;
    }
  }
}

.reset-scroll {
  position: absolute;
  display: flex;
  align-items: center;
  bottom: 10%;
  right: 5%;
  border-style: solid;
  border-radius: 5px;
  border-color: var(--color-action-background-primary);
  color: var(--color-action-background-primary);
  box-shadow: 0px 0px 12px 4px #373737;
  background: #808080;
  color: white;
  &:hover {
    background-color: var(--color-action-background-primary);
    color: white;
    cursor: pointer;
  }
}

@keyframes wave-lines {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}
.skeleton-text, .skeleton-thumb, .skeleton-content {
  width: 100%;
  margin-bottom: 0.5rem;
  background: rgba(130, 130, 130, 0.2);
  background: -webkit-gradient(linear, left top, right top, color-stop(8%, rgba(130, 130, 130, 0.2)), color-stop(18%, rgba(130, 130, 130, 0.3)), color-stop(33%, rgba(130, 130, 130, 0.2)));
  background: linear-gradient(to right, rgba(130, 130, 130, 0.2) 8%, rgba(130, 130, 130, 0.3) 18%, rgba(130, 130, 130, 0.2) 33%);
  background-size: 800px 100px;
  animation: wave-lines 2s infinite ease-out;
}
.skeleton-thumb {
  height: 50%;
}
.skeleton-text {
  height: 2rem;
  border-radius: var(--border-radius);
}
.skeleton-content {
  height: 100%;
  border-radius: var(--border-radius);
}
</style>